import { AppShell, Burger, Group, NavLink, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AUTH_MODE } from './lib/auth';
import { useSession } from './lib/session';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { InboxPage } from './pages/InboxPage';
import { RegisterPage } from './pages/RegisterPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignInPage } from './pages/SignInPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';

// The two links the server mails. Reachable signed out and signed in alike,
// because somebody following one out of their inbox may be either.
const VERIFY_PATH = '/verify-email';
const SET_PASSWORD_PATH = '/set-password';

// Three places: the inbox, the numbers, and the one page where the agent is
// tuned. The source's console had a fourth for the team and a fifth for
// accepting an invitation; a workspace owner seats a colleague by account id
// instead.
export function App() {
  const { signedIn, orgId, signOut } = useSession();
  const [opened, { toggle }] = useDisclosure();
  const { pathname } = useLocation();

  if (pathname === VERIFY_PATH || pathname === SET_PASSWORD_PATH) {
    return (
      <Routes>
        <Route path={VERIFY_PATH} element={<VerifyEmailPage />} />
        <Route path={SET_PASSWORD_PATH} element={<SetPasswordPage />} />
      </Routes>
    );
  }
  if (!signedIn && AUTH_MODE === 'authit' && pathname === '/register') return <RegisterPage />;
  if (!signedIn || !orgId) return <SignInPage />;

  return (
    <AppShell
      header={{ height: 52 }}
      navbar={{ width: 200, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding={pathname === '/inbox' ? 0 : 'md'}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={700}>Inbox</Text>
          </Group>
          <NavLink component="button" label="Sign out" w="auto" onClick={signOut} />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {[
          { to: '/inbox', label: 'Inbox' },
          { to: '/analytics', label: 'How it is going' },
          { to: '/settings', label: 'Settings' },
        ].map((item) => (
          <NavLink
            key={item.to}
            component={Link}
            to={item.to}
            label={item.label}
            active={pathname.startsWith(item.to)}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
